import { memo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { TouchableRipple } from 'react-native-paper';

import {
  ActionLinkRow,
  type ActionLinkItem,
} from '@/components/molecules/action-link/ActionLinkRow';
import { StatusBadge } from '@/components/atoms/badge';
import { type BadgeItem } from '@/components/molecules/badge/BadgeRow';
import { formatDate } from '@/formatters/formatDate';
import { formatDateOnly } from '@/formatters/formatDateOnly';
import { formatAttendeesSummary } from '@/formatters/formatAttendeesSummary';
import { formatTimeOnly } from '@/formatters/formatTimeOnly';
import { type GetMeetingsQuery } from '@/generated/usermobile-bff/graphql';
import { useTranslation } from '@/i18n';
import { radii, rippleOnSurface, spacing, unisonColors, useAppTheme } from '@/theme';

interface MeetingListItemProps {
  readonly item: GetMeetingsQuery['meetings']['data'][number];
  readonly hasPendingUpload?: boolean;
  readonly hasFailedUpload?: boolean;
  readonly onPress: () => void;
}

const getBadges = (
  item: GetMeetingsQuery['meetings']['data'][number],
  t: (key: string, options?: Record<string, unknown>) => string,
  hasPendingUpload?: boolean,
  hasFailedUpload?: boolean,
): BadgeItem[] => {
  const badges: BadgeItem[] = [];
  const summaryStatus = item.recording?.summary?.status;
  const hasMissingAttendees = item.attendees.length === 0;
  const hasSummaryInProgress = summaryStatus === 'PENDING' || summaryStatus === 'PROCESSING';

  if (hasPendingUpload) {
    badges.push({
      variant: 'error',
      label: t('meetings.pendingUpload'),
    });
  }

  if (hasFailedUpload) {
    badges.push({
      variant: 'error',
      label: t('meetings.uploadFailed'),
    });
  }

  if (!hasPendingUpload && !hasFailedUpload && summaryStatus === 'COMPLETED') {
    badges.push({
      variant: 'success',
      label: t('meetings.aiSummaryGenerated'),
    });
  }

  if (!hasPendingUpload && !hasFailedUpload && hasSummaryInProgress) {
    badges.push({
      variant: 'info',
      label: t('meetings.aiSummaryInProgress'),
      loading: true,
    });
  }

  if (!hasPendingUpload && !hasFailedUpload && summaryStatus === 'FAILED') {
    badges.push({
      variant: 'error',
      label: t('meetings.aiSummaryUnavailable'),
    });
  }

  if (!hasPendingUpload && !hasFailedUpload && summaryStatus === 'ON_HOLD') {
    badges.push({
      variant: 'error',
      label: t('meetings.aiSummaryOnHold'),
    });
  }

  if (!item.recording && !hasPendingUpload && !hasFailedUpload) {
    badges.push({
      variant: 'neutral',
      label: t('meetings.noRecording'),
    });
  }

  if (hasMissingAttendees) {
    badges.push({
      variant: 'warning',
      label: t('meetings.missingAttendees'),
    });
  }

  return badges;
};

const getActions = ({
  hasPendingUpload,
  hasFailedUpload,
  hasMissingAttendees,
  onPress,
  t,
}: {
  readonly hasPendingUpload?: boolean;
  readonly hasFailedUpload?: boolean;
  readonly hasMissingAttendees: boolean;
  readonly onPress: () => void;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
}): ActionLinkItem[] => {
  if (!hasPendingUpload && !hasFailedUpload && !hasMissingAttendees) {
    return [];
  }

  return [{ label: t('meetings.fixIssues'), onPress }];
};

const MeetingListItemBase = ({
  item,
  hasPendingUpload,
  hasFailedUpload,
  onPress,
}: MeetingListItemProps) => {
  const { locale, t } = useTranslation();
  const { colors } = useAppTheme();
  const hasMissingAttendees = item.attendees.length === 0;
  const title = item.name ?? formatDate(item.startsAt, locale);
  const date = formatDateOnly(item.startsAt);
  const time = formatTimeOnly(item.startsAt);
  const attendeeNames = item.attendees.flatMap(({ contact }) =>
    contact?.displayName ? [contact.displayName] : [],
  );
  const subtitle = formatAttendeesSummary(attendeeNames, t);
  const badges = getBadges(item, t, hasPendingUpload, hasFailedUpload);
  const actions = getActions({
    hasFailedUpload,
    hasMissingAttendees,
    hasPendingUpload,
    onPress,
    t,
  });

  return (
    <View style={styles.container} testID="meeting-list-item">
      <TouchableRipple
        accessibilityRole="button"
        rippleColor={rippleOnSurface}
        style={[styles.content, actions.length === 0 && styles.contentNoActions]}
        onPress={onPress}
        testID="meeting-list-item-pressable">
        <View style={styles.contentInner}>
          <View style={styles.headerRow}>
            <View style={styles.badgesContainer}>
              {badges.map((badge) => (
                <StatusBadge
                  key={`${badge.label}-${badge.variant}`}
                  label={badge.label}
                  loading={badge.loading}
                  variant={badge.variant}
                />
              ))}
            </View>
            <View style={styles.dateTimeContainer}>
              <Text style={[styles.time, { color: colors.textNdSecondary }]}>{date}</Text>
              <Text style={[styles.time, { color: colors.textNdSecondary }]}>{time}</Text>
            </View>
          </View>
          <View style={styles.bodyContent}>
            <Text style={[styles.title, { color: colors.textNdPrimary }]}>{title}</Text>
            {subtitle ? (
              <Text style={[styles.subtitle, { color: colors.textNdSecondary }]}>{subtitle}</Text>
            ) : null}
          </View>
        </View>
      </TouchableRipple>
      {actions.length > 0 ? (
        <View style={styles.actionsContainer}>
          <ActionLinkRow actions={actions} />
        </View>
      ) : null}
    </View>
  );
};

export const MeetingListItem = memo(MeetingListItemBase);

const styles = StyleSheet.create({
  container: {
    backgroundColor: unisonColors.white,
    borderRadius: radii.md,
    overflow: 'hidden',
  },
  content: {
    paddingTop: spacing[4],
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[2],
  },
  contentNoActions: {
    paddingBottom: spacing[4],
  },
  contentInner: {
    rowGap: spacing[2],
  },
  actionsContainer: {
    paddingHorizontal: spacing[4],
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    columnGap: spacing[3],
  },
  badgesContainer: {
    flex: 1,
    rowGap: spacing[1],
  },
  bodyContent: {
    rowGap: spacing[1],
  },
  title: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0.25,
  },
  dateTimeContainer: {
    alignItems: 'flex-end',
  },
  time: {
    fontSize: 11,
    fontWeight: '500',
    lineHeight: 16,
    letterSpacing: 0.5,
    textAlign: 'right',
  },
});
